import React, { useState, useEffect } from 'react';
import { useUsers } from './UsersContext';
import { T, Th, Tr, ActionBtn, Modal, UserDetailFields, TableCard, TableBar, SearchInput, Toast, RejectModal, PrimaryBtn, statusBadge, visibilityBadge } from './theme';
import { useRefresh } from './RefreshContext';

const API_BASE = 'https://d3bdqxkpn753kf.cloudfront.net';

const AdminArticlesTab = () => {
  const { getUserName, getUserObj } = useUsers();
  const { refresh, triggerRefresh } = useRefresh();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [viewUser, setViewUser] = useState(null);
  const [viewArticle, setViewArticle] = useState(null);
  const [search, setSearch] = useState('');
  const [viewReview, setViewReview] = useState(null);
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectComment, setRejectComment] = useState('');

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/vrv-articlecontent-getAll`, { method: 'GET' });
      const data = await res.json();
      const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
      setArticles((parsed.data || []).sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)));
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchArticles(); }, [refresh]); // eslint-disable-line

  const showMsg = (message, type) => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3500);
  };

  const toggleSelect = id => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleSelectAll = () => setSelectedIds(selectedIds.length === articles.length ? [] : articles.map(a => a.content_id));

  const handleBulkAction = async (action, comments = '') => {
    if (selectedIds.length === 0) { showMsg('Select at least one item', 'error'); return; }
    if ((action === 'PUBLIC' || action === 'PRIVATE') && articles.some(a => selectedIds.includes(a.content_id) && a.status !== 'APPROVED')) { showMsg('Cannot change visibility for non-approved content', 'error'); return; }
    if (action === 'REJECT' && !comments) { setRejectModal(true); return; }
    try {
      const token = localStorage.getItem('vrvToken');
      const reviewerName = localStorage.getItem('vrvName') || localStorage.getItem('vrvEmail') || '';
      let allSuccess = true;
      for (const content_id of selectedIds) {
        const payload = { content_id, action };
        if (action === 'APPROVE') { payload.reviewedBy = reviewerName; payload.reviewedAt = new Date().toISOString(); payload.reviewComments = 'APPROVED'; }
        if (action === 'REJECT') { payload.reviewedBy = reviewerName; payload.reviewedAt = new Date().toISOString(); payload.reviewComments = comments; }
        const res = await fetch(`${API_BASE}/vrv-articlecontent-action`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify(payload) });
        if (!res.ok) allSuccess = false;
      }
      showMsg(allSuccess ? `${action} applied to ${selectedIds.length} item(s)` : 'Some actions failed', allSuccess ? 'success' : 'error');
      setSelectedIds([]); fetchArticles(); triggerRefresh();
    } catch { showMsg('Error connecting to server', 'error'); }
  };

  const filteredArticles = articles.filter(a => search === '' || Object.values(a).some(val => String(val).toLowerCase().includes(search.toLowerCase())));

  return (
    <div>
      <div className="mb-4"><SearchInput value={search} onChange={e => setSearch(e.target.value)} /></div>
      {loading && <p className="text-center py-8 text-sm" style={{ color: T.muted }}>Loading...</p>}
      {!loading && articles.length > 0 && (
        <>
          {/* Desktop Table */}
          <div className="desktop-table">
            <TableCard>
              <TableBar className="table-bar-wrap">
                <p className="text-xs font-bold" style={{ color: T.muted }}>{selectedIds.length > 0 ? `${selectedIds.length} selected` : `${filteredArticles.length} articles`}</p>
                <div className="flex flex-wrap gap-2">
                  {[['Public', 'gold'], ['Private', 'muted'], ['Approve', 'green'], ['Reject', 'muted'], ['Delete', 'red']].map(([label, color]) => (
                    <ActionBtn key={label} onClick={() => handleBulkAction(label.toUpperCase())} disabled={selectedIds.length === 0} color={color}>{label}</ActionBtn>
                  ))}
                </div>
              </TableBar>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr>
                    <th className="px-3 py-3 w-8" style={{ backgroundColor: T.stripe, borderBottom: `1px solid ${T.border}` }}><input type="checkbox" checked={selectedIds.length === articles.length && articles.length > 0} onChange={toggleSelectAll} /></th>
                    <Th>Title</Th><Th>Created By</Th><Th>Date</Th><Th>Status</Th><Th>Visibility</Th><Th center>View</Th><Th center>Review</Th>
                  </tr></thead>
                  <tbody>
                    {filteredArticles.map((article, i) => (
                      <Tr key={article.content_id || i} selected={selectedIds.includes(article.content_id)}>
                        <td className="px-3 py-2.5"><input type="checkbox" checked={selectedIds.includes(article.content_id)} onChange={() => toggleSelect(article.content_id)} /></td>
                        <td className="px-3 py-2.5 font-medium" style={{ color: T.ink }}>{article.title || '—'}</td>
                        <td className="px-3 py-2.5">{article.createdBy ? <button onClick={() => { const u = getUserObj(article.createdBy); if (u) setViewUser(u); }} className="font-bold hover:underline" style={{ color: T.gold }}>{getUserName(article.createdBy) || '...'}</button> : '—'}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap" style={{ color: T.muted }}>{article.createdAt ? new Date(article.createdAt).toLocaleDateString() : '—'}</td>
                        <td className="px-3 py-2.5"><span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest" style={statusBadge(article.status)}>{article.status || '—'}</span></td>
                        <td className="px-3 py-2.5"><span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest" style={visibilityBadge(article.visibility)}>{article.visibility}</span></td>
                        <td className="px-3 py-2.5 text-center"><button onClick={() => setViewArticle(article)} className="font-black text-[10px] uppercase tracking-widest hover:underline" style={{ color: T.dark }}>View</button></td>
                        <td className="px-3 py-2.5 text-center">{article.reviewedBy ? <button onClick={() => setViewReview(article)} className="font-black text-[10px] uppercase tracking-widest hover:underline" style={{ color: T.gold }}>Review</button> : '—'}</td>
                      </Tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TableCard>
          </div>

          {/* Mobile Cards */}
          <div className="mobile-cards">
            <div className="flex flex-wrap gap-2 mb-3">
              {[['Public', 'gold'], ['Private', 'muted'], ['Approve', 'green'], ['Reject', 'muted'], ['Delete', 'red']].map(([label, color]) => (
                <ActionBtn key={label} onClick={() => handleBulkAction(label.toUpperCase())} disabled={selectedIds.length === 0} color={color}>{label}</ActionBtn>
              ))}
            </div>
            {filteredArticles.map((article, i) => (
              <div key={article.content_id || i} className="vrv-mobile-card" style={{ border: selectedIds.includes(article.content_id) ? `1.5px solid ${T.gold}` : undefined }}>
                <div className="flex items-start gap-3 mb-2">
                  <input type="checkbox" checked={selectedIds.includes(article.content_id)} onChange={() => toggleSelect(article.content_id)} className="mt-1" />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm truncate" style={{ color: T.navy }}>{article.title || '—'}</p>
                    <p className="text-xs" style={{ color: T.muted }}>{article.createdAt ? new Date(article.createdAt).toLocaleDateString() : '—'}</p>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest flex-shrink-0" style={statusBadge(article.status)}>{article.status || '—'}</span>
                </div>
                <div className="flex items-center gap-2 text-xs mb-3">
                  <span style={{ color: T.muted }}>By: </span>
                  {article.createdBy ? <button onClick={() => { const u = getUserObj(article.createdBy); if (u) setViewUser(u); }} className="font-bold" style={{ color: T.gold }}>{getUserName(article.createdBy) || '...'}</button> : '—'}
                  <span className="ml-auto px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase" style={visibilityBadge(article.visibility)}>{article.visibility}</span>
                </div>
                <div className="flex gap-2 pt-2" style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                  <ActionBtn onClick={() => setViewArticle(article)} color="muted" small>View</ActionBtn>
                  {article.reviewedBy && <ActionBtn onClick={() => setViewReview(article)} color="gold" small>Review</ActionBtn>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      {!loading && articles.length === 0 && <p className="text-center py-8 text-sm" style={{ color: T.muted }}>No articles found.</p>}

      {viewArticle && (
        <Modal onClose={() => setViewArticle(null)} title={viewArticle.title} subtitle="Article Preview" wide>
          <p className="text-xs mb-4" style={{ color: T.muted }}>By {viewArticle.createdBy || 'Unknown'} · {viewArticle.createdAt ? new Date(viewArticle.createdAt).toLocaleDateString() : ''}</p>
          <p className="text-sm leading-relaxed whitespace-pre-wrap mb-6" style={{ color: T.ink, borderTop: `1px solid ${T.border}`, paddingTop: '1rem' }}>{viewArticle.content}</p>
          <PrimaryBtn onClick={() => setViewArticle(null)}>Close</PrimaryBtn>
        </Modal>
      )}
      {viewUser && <Modal onClose={() => setViewUser(null)} title="User Details" subtitle="Member Info" wide><UserDetailFields user={viewUser} /><div className="mt-6"><PrimaryBtn onClick={() => setViewUser(null)}>Close</PrimaryBtn></div></Modal>}
      {viewReview && (
        <Modal onClose={() => setViewReview(null)} title="Review Details" subtitle="Review Info">
          {[['Reviewed By', viewReview.reviewedBy], ['Reviewed At', viewReview.reviewedAt ? new Date(viewReview.reviewedAt).toLocaleString() : '—'], ['Comments', viewReview.reviewComments || 'No comments']].map(([label, val]) => (
            <div key={label} className="mb-3">
              <p className="text-[10px] font-black uppercase tracking-widest mb-1" style={{ color: T.muted }}>{label}</p>
              <p className="text-sm font-medium px-3 py-2 rounded-lg whitespace-pre-wrap" style={{ backgroundColor: T.stripe, color: T.ink, border: `1px solid ${T.border}` }}>{val}</p>
            </div>
          ))}
          <div className="mt-4"><PrimaryBtn onClick={() => setViewReview(null)}>Close</PrimaryBtn></div>
        </Modal>
      )}
      {rejectModal && <RejectModal value={rejectComment} onChange={e => setRejectComment(e.target.value)} onCancel={() => { setRejectModal(false); setRejectComment(''); }} onConfirm={() => { handleBulkAction('REJECT', rejectComment || 'Rejected by admin'); setRejectModal(false); setRejectComment(''); }} />}
      <Toast toast={toast} onClose={() => setToast({ show: false, message: '', type: '' })} />
    </div>
  );
};

export default AdminArticlesTab;
